import { ApolloLink, Observable } from 'apollo-link';
import { print } from 'graphql';
import { createClient } from 'graphql-ws';

// Bridges a modern graphql-ws client to Apollo Client 2's ApolloLink interface.
// graphql-ws replaces the deprecated subscriptions-transport-ws transport: it keeps the
// WebSocket alive with ping/pong heartbeats, detects dead "zombie" sockets, and transparently
// reconnects (re-running connectionParams for a fresh token). That keeps the dashboard's live
// subscriptions from silently going stale behind proxy idle timeouts or backgrounded tabs.
class GraphQLWsLink extends ApolloLink {
  constructor(client) {
    super();
    this.wsClient = client;
  }

  request(operation) {
    return new Observable(observer => {
      return this.wsClient.subscribe(
        {
          operationName: operation.operationName,
          query: print(operation.query),
          variables: operation.variables,
          extensions: operation.extensions
        },
        {
          next: result => observer.next(result),
          error: err => {
            if (Array.isArray(err)) {
              // GraphQL errors array from the server.
              observer.error(new Error(err.map(e => (e && e.message) || String(e)).join(', ')));
            } else if (err instanceof Error) {
              observer.error(err);
            } else if (err && typeof err === 'object' && (err.code !== undefined || err.reason !== undefined)) {
              // CloseEvent (socket closed); surface a readable message for onError handling.
              observer.error(new Error(err.reason || `WebSocket closed (code ${err.code})`));
            } else {
              observer.error(err);
            }
          },
          complete: () => observer.complete()
        }
      );
    });
  }
}

// Creates an ApolloLink backed by graphql-ws. The returned link exposes `wsClient` so callers
// can `terminate()` (force reconnect with a fresh token) or `dispose()` (permanent close on logout).
export const createGraphqlWsLink = ({ url, connectionParams, retryAttempts = Infinity, keepAlive = 10000 }) => {
  let activeSocket = null;
  let pongTimeout = null;

  const client = createClient({
    url,
    connectionParams,
    lazy: true,
    keepAlive,
    retryAttempts,
    shouldRetry: () => true,
    on: {
      connected: socket => {
        activeSocket = socket;
      },
      ping: received => {
        // We sent a ping; if no pong comes back shortly, the socket is dead — close it so
        // graphql-ws reconnects instead of silently delivering stale (or no) data.
        if (!received) {
          pongTimeout = setTimeout(() => {
            if (activeSocket && activeSocket.readyState === 1 /* OPEN */) {
              activeSocket.close(4408, 'Request Timeout');
            }
          }, 5000);
        }
      },
      pong: received => {
        if (received && pongTimeout) {
          clearTimeout(pongTimeout);
          pongTimeout = null;
        }
      }
    }
  });

  return new GraphQLWsLink(client);
};
